import { For, Show, type Component } from 'solid-js';

import { openStation, teleportToStation } from '../net/protocol';
import type { GameStore } from '../state/store';
import { stationAccent } from '../game/zone/biomes';
import { cssColour } from '../game/zone/textures';
import { iconPath } from './icons';
import { actionTitle, townGroups, type TownAction } from './townbar';

/**
 * The town's one navigation bar: everywhere you can go and everything you can do, in
 * three named categories.
 *
 * It replaces three separate floating widgets — the `[ GRIND ]` sigil, the *Back to the
 * Run* sigil beneath it, and the workshop row beneath that — which between them answered
 * *what can I do in town* in three visual languages and no stated relationship. A
 * workshop, the stash and the run are the same kind of thing from where the player sits,
 * and the bar says so. See `ui/townbar.ts` for the grouping and why it is pure.
 *
 * **Town only.** In a zone the bar is gone entirely and the way home is the *Open Town
 * Portal* button in `GrindPortal`, which is a different action with a different
 * consequence — it is not travel, it is opening a gate — and does not belong in a bar of
 * places.
 *
 * A click on a station does three things in this order: stand there, ask to trade there,
 * then show the panel. The teleport is refused server-side outside town, so the panel
 * never opens over a player who was not moved, and the `CMD_OPEN_STATION` behind it means
 * **one** server-side authority answers *may this be open*, whether the player pressed a
 * button or walked up. Commands arrive in order on one socket, so the teleport lands
 * first and its own range check passes. `game/keyboard.ts` performs the identical three
 * steps, which is what keeps the key and the button honest with each other.
 */
export const TownBar: Component<{
  store: GameStore;
  /**
   * Walks to the standing return gate and steps through.
   *
   * Passed in rather than reached for, exactly as `GrindPortal` takes it: walking belongs
   * to the `Navigator`, and a HUD bar that owned a route would be a second thing steering
   * the player.
   */
  onWalkToGate?: (gate: { x: number; z: number }) => void;
}> = (props) => {
  const groups = () =>
    townGroups(props.store.stations(), { hasReturnGate: props.store.portals().length > 0 });

  const run = (action: TownAction) => {
    switch (action.action.type) {
      case 'station': {
        const kind = action.action.station;
        props.store.send(teleportToStation(kind));
        props.store.send(openStation(kind));
        props.store.openStation(kind);
        return;
      }
      case 'grind':
        props.store.openMenu('portal');
        return;
      case 'resume': {
        const gate = props.store.portals()[0];
        if (!gate) return;
        props.onWalkToGate?.(gate);
        return;
      }
    }
  };

  return (
    <Show when={props.store.inHub()}>
      <nav class="townbar hud-interactive" data-testid="town-bar" aria-label="Town">
        <For each={groups()}>
          {(group) => (
            <div class={`townbar__group townbar__group--${group.kind}`} data-testid={`town-group-${group.kind}`}>
              <span class="townbar__label">{group.label}</span>
              <div class="townbar__actions">
                <For each={group.actions}>
                  {(action) => {
                    const path = iconPath(action.icon);
                    // Only a station can be "open"; travel actions are verbs, not places
                    // with a panel, so they are never shown as pressed.
                    const open = () =>
                      action.action.type === 'station' &&
                      props.store.station() === action.action.station;

                    return (
                      <button
                        type="button"
                        class="townbar__button"
                        classList={{ 'townbar__button--open': open() }}
                        data-testid={`town-action-${action.id}`}
                        aria-label={`${action.label} — ${action.detail}`}
                        aria-pressed={open()}
                        title={actionTitle(action)}
                        onClick={() => run(action)}
                      >
                        <svg
                          class="townbar__glyph"
                          viewBox={`0 0 ${path.width} ${path.height}`}
                          aria-hidden="true"
                        >
                          <path d={path.d} />
                        </svg>

                        {/* Travel is named; a workshop is not. Five bronze glyphs in a row
                            are learnable because each one also carries its building's
                            colour below, but *Grind* and *Back to the Run* are one-offs
                            with no building to match, so they say what they are. */}
                        <Show when={group.kind === 'travel'}>
                          <span class="townbar__name">{action.label}</span>
                        </Show>

                        {/* The shop's own colour, from the same table its banner and
                            counter cloth are painted with — a swatch that matches the stall
                            says which building across the square this is, where five bronze
                            icons say nothing. Absolutely positioned, so it changes no box. */}
                        <Show when={action.action.type === 'station'}>
                          <span
                            class="townbar__accent"
                            style={{
                              background: cssColour(
                                stationAccent(
                                  (action.action as { station: string }).station as never,
                                ),
                              ),
                            }}
                            aria-hidden="true"
                          />
                        </Show>

                        <Show when={action.key}>
                          <span class="townbar__key">{action.key!.toUpperCase()}</span>
                        </Show>
                      </button>
                    );
                  }}
                </For>
              </div>
            </div>
          )}
        </For>

        {/*
          **No purse here.** It was a small figure at the end of this bar, floating over
          the world with nothing around it to give it meaning — reported as *weird*, and it
          was: a purse is consulted in exactly two places, the shop (where it is the
          constraint on what you can buy) and the bag (where it is what a full inventory
          converts into). Beside a row of workshop icons it was neither. See `Purse.tsx`.
        */}
      </nav>
    </Show>
  );
};
